import React, { useContext, useMemo, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import * as styles from '../check-in-employees.styles';
import OfficeManagerListCard from '../cards/office-list-card.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../core/components/center/center.component';
import Column from '../../../core/components/column/column.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import type Office from '../../offices/models/office';
import Grid from '../../../core/components/grid/grid.component';
import SearchBar from '../../../core/components/search-bar/search-bar.component';
import { MainAxisAlignment, CrossAxisAlignment } from '../../../core/components/flex/flex.component';

interface Props {
  readonly allOffices: Office[] | undefined;
  readonly officeChange: (selectedOffice: Office) => void;
  readonly selectedOfficeId: string | undefined;
  readonly isLoading: boolean;
}

const OfficeOverviewGroupOfficeManager = ({ allOffices, officeChange, selectedOfficeId, isLoading }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  /** Text entered into the search bar, lowercased for case-insensitive matching. */
  const [searchQuery, setSearchQuery] = useState('');

  /** All offices sorted alphabetically – recomputed only when allOffices changes. */
  const sortedOffices = useMemo(
    () => (allOffices ?? []).sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)),
    [allOffices],
  );

  /**
   * Offices that match the current search query.
   * Filtering is done client-side on the already-loaded list so no extra
   * network request is needed.  The result is memoised to avoid recomputing
   * on every render cycle.
   */
  const filteredOffices = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (trimmed === '') return sortedOffices;
    return sortedOffices.filter((office) => office.name.toLowerCase().includes(trimmed));
  }, [sortedOffices, searchQuery]);

  /** Whether to show the search bar – only useful when there are multiple offices. */
  const showSearch = sortedOffices.length > 5;

  return (
    <div className={style(styles.sectionCard(isMobile))}>
      <Column>
        {/* ---- Header row: title + live office count ---- */}
        <Row
          mainAxisAlignment={MainAxisAlignment.SpaceBetween}
          crossAxisAlignment={CrossAxisAlignment.Center}
          className={style({ marginBottom: 8 })}
        >
          <span className={style(styles.columnHeader(isMobile))}>{t('offices').toUpperCase()}</span>
          {!isLoading && sortedOffices.length > 0 && (
            <span
              style={{
                fontSize: StyleConstants.SECONDARY_FONT_SIZE,
                color: StyleConstants.GREY_COLOR,
                fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
                whiteSpace: 'nowrap',
              }}
            >
              {/* Show "X / Y" when filtering, otherwise just "Y" */}
              {searchQuery.trim() !== ''
                ? `${filteredOffices.length} / ${sortedOffices.length}`
                : sortedOffices.length}
            </span>
          )}
        </Row>

        {/* ---- Search bar (only shown once enough offices are loaded) ---- */}
        {!isLoading && showSearch && (
          <div style={{ marginBottom: 12 }}>
            <SearchBar
              placeholder={t('search-by-name') as string}
              onSearch={setSearchQuery}
              maxLength={100}
            />
          </div>
        )}

        {((): React.JSX.Element => {
          if (isLoading) {
            return (
              <Center>
                <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
              </Center>
            );
          }

          if (sortedOffices.length === 0) {
            return <span className={style(styles.emptyListMessage)}>{t('no-offices-available')}</span>;
          }

          if (filteredOffices.length === 0) {
            // The user has typed a search query that matches nothing.
            return <span className={style(styles.emptyListMessage)}>{t('no-offices-available')}</span>;
          }

          return (
            <Grid
              rowClass={style(styles.gridRow)}
              numberOfColumns={isMobile ? 1 : 3}
              itemSpacing={12}
              numberOfItems={filteredOffices.length}
              renderItem={(index): React.JSX.Element | undefined => {
                const office = filteredOffices[index];
                return (
                  office && (
                    <div className={isMobile ? style(styles.rowItem(true)) : undefined} key={office.id}>
                      <OfficeManagerListCard office={office} selectedOfficeChange={officeChange} selectedOffice={selectedOfficeId} />
                    </div>
                  )
                );
              }}
            />
          );
        })()}
      </Column>
    </div>
  );
};

export default OfficeOverviewGroupOfficeManager;
